import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '../lib/swal';
import { Loader2, Eye, EyeOff, Lock, Mail, Home } from 'lucide-react';
import websocketService from '../services/websocket';

const Login = () => {
  const navigate = useNavigate();
  const { login, user, handleGoogleCallback, loading: authLoading } = useAuth();
  const isMounted = useRef(true);
  
  const [email, setEmail] = useState(localStorage.getItem('fastCheckoutEmail') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ logo_url: '/logo.png' });

  useEffect(() => {
    fetchBranding();
    
    // Escuchar cambios de branding en vivo
    websocketService.on('branding_updated', (data) => {
      if (data.logo_url) {
        // Añadir timestamp para evitar cache
        const freshUrl = `${data.logo_url}?t=${Date.now()}`;
        setBranding(prev => ({ ...prev, logo_url: freshUrl }));
      }
    });
  }, []);

  const fetchBranding = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      const response = await axios.get(`${API}/settings/branding`);
      if (response.data && response.data.logo_url) {
        setBranding(response.data);
      }
    } catch (error) {
      console.error('Error al cargar branding:', error);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authLoading && user && isMounted.current) {
      redirectBasedOnRole(user.role);
    }
  }, [user, authLoading]);

  useEffect(() => {
    const processingRef = { current: false };
    
    const processGoogleCallback = async () => {
      if (processingRef.current) return;
      
      if (window.location.hash.includes('session_id=')) {
        processingRef.current = true;
        
        if (isMounted.current) {
          setLoading(true);
        }
        
        try {
          const userData = await handleGoogleCallback();
          if (isMounted.current) {
            toast.success('¡Bienvenido de nuevo!', `Has iniciado sesión como ${userData.name}`);
            // La redirección se hará automáticamente por el useEffect anterior
          }
        } catch (error) {
          if (isMounted.current) {
            toast.error('Error al iniciar sesión', 'No se pudo completar el inicio de sesión con Google');
            setLoading(false);
          }
        }
      }
    };
    
    processGoogleCallback();
  }, [handleGoogleCallback]);

  const redirectBasedOnRole = (role) => {
    if (!isMounted.current) return;
    
    if (role === 'admin') {
      navigate('/admin');
    } else if (role === 'vendedor') {
      navigate('/vendedor');
    } else {
      navigate('/usuario');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Campos requeridos', 'Por favor ingresa tu email y contraseña');
      return;
    }

    if (isMounted.current) {
      setLoading(true);
    }

    try {
      const userData = await login(email, password);
      if (isMounted.current) {
        toast.success('¡Bienvenido de nuevo!', 'Has iniciado sesión exitosamente');
        redirectBasedOnRole(userData.role);
      }
    } catch (error) {
      if (isMounted.current) {
        toast.error('Error al iniciar sesión', error.response?.data?.detail || 'Email o contraseña incorrectos');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#020202] px-4 py-8 transition-colors duration-500">
      {/* Fondo de la sección */}
      <div className="absolute inset-0 z-0">
        <img src="/assets/fondo2.png" alt="Promo Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70"></div>
      </div>
      <div className="w-full max-w-5xl relative z-10 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-16 px-4">
        {/* Columna Izquierda: Logo y Textos */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex justify-center md:justify-start mb-6 md:mb-8">
            <Link to="/" className="relative group inline-block cursor-pointer">
              <img 
                src="/logo.png" 
                alt="WishWay Logo" 
                className="relative h-16 md:h-24 lg:h-32 w-auto object-contain transition-transform duration-500 hover:scale-110 drop-shadow-xl"
              />
            </Link>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white drop-shadow-md mb-2 md:mb-4 leading-tight tracking-tight">
            Bienvenido <br className="hidden md:block" /> de vuelta
          </h1>
          <p className="text-gray-300 font-medium text-lg md:text-xl">
            Inicia sesión en tu cuenta
          </p>
        </div>

        {/* Columna Derecha: Formulario */}
        <div className="w-full max-w-md flex-none">
          {/* Card Premium */}
          <Card className="border border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.8)] bg-black/40 backdrop-blur-xl rounded-[22px]">
          <CardContent className="p-8">
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email Input */}
              <div className="space-y-2">
                <Label 
                  htmlFor="email" 
                  className="text-sm font-bold text-white/90"
                >
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-12 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password Input with Toggle */}
              <div className="space-y-2">
                <Label 
                  htmlFor="password" 
                  className="text-sm font-bold text-white/90"
                >
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-12 h-12 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium text-[#fca050] hover:text-[#f24e00] transition-colors"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>

              {/* Login Button */}
              <Button 
                type="submit" 
                className="w-full py-4 rounded-xl bg-gradient-to-b from-[#fca050] to-[#f24e00] text-[#3f2506] font-black uppercase tracking-widest text-[13px] shadow-[0_6px_0_#b35a00] border-t border-white/40 hover:brightness-110 active:translate-y-1 active:shadow-none transition-all relative overflow-hidden"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Iniciando sesión...
                  </div>
                ) : (
                  'Iniciar sesión'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0a0a0a] text-gray-400 rounded-full">O continúa con</span>
              </div>
            </div>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border border-white/20 bg-white/5 hover:bg-white/10 text-white hover:text-white transition-all duration-200 rounded-xl"
              onClick={handleGoogleLogin}
              disabled={loading}
              data-testid="google-login-btn"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="font-medium">Continuar con Google</span>
            </Button>

            {/* Sign Up Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                ¿No tienes cuenta?{' '}
                <Link 
                  to="/register" 
                  className="font-bold text-[#fca050] hover:text-[#f24e00] transition-colors"
                >
                  Regístrate gratis
                </Link>
              </p>
            </div>

            {/* Back to Home */}
            <div className="mt-6">
              <Link 
                to="/" 
                className="flex items-center justify-center gap-2 w-full py-3 border border-white/10 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-all duration-200 rounded-xl text-sm font-medium"
              >
                <Home className="w-4 h-4" /> Volver al inicio
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6 relative z-10">
          Al continuar, aceptas nuestros términos de servicio y política de privacidad
        </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
